import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Collapse,
  DatePicker,
  Form,
  Input,
  Select,
  Table,
} from 'antd';
import dayjs from 'dayjs';
import { API } from '@services/api.service';
import AlertBox from '@/components/alert-box';
import { useNotify } from '@/hooks';
import { DATE_FORMATS } from '@/utils/formUtils';
import { saveBlobAsFile } from '@/utils/exportUtils';
import { buildCountryOptions } from './constants';

const DOMESTIC_FIELDS = [
  'perDiemExpense',
  'costExpense',
  'fuelExpense',
  'tollExpense',
  'parkingExpense',
  'advance',
  'liability',
];
const FOREIGN_FIELDS = [
  'perDiemExpense',
  'costExpense',
  'advance',
  'liability',
  'otherCurrencyLiability',
];

const ACCOUNT_RULE = { pattern: /^\d{3,10}$/ };

const formatAmount = (value) =>
  value
    ? new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(value))
    : '';

const errorOf = (error) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message;

/**
 * The approved travel order as a journal for AB Soft (NALOG.xls).
 *
 * The lines are built on the server from the settlement; this panel shows
 * them, lets the accountant set the company's accounts (per country abroad)
 * and downloads the file for AB's „Preuzimanje naloga iz Nalog.xls". The
 * accounts and folder used are remembered for the company's next order.
 * A company whose journals go to BizniSoft (`preview.format`) gets BizniSoft's
 * NALOG.xlsx instead, without a folder.
 */
export default function TravelAbJournal({ requestId }) {
  const { t, i18n } = useTranslation(['officialTravel', 'messages']);
  const notification = useNotify();
  const [form] = Form.useForm();
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [openPanels, setOpenPanels] = useState([]);

  const countryOptions = useMemo(
    () => buildCountryOptions(i18n.language),
    [i18n.language]
  );

  const loadPreview = async (accounts) => {
    try {
      setIsLoading(true);
      const result = await API.requests.previewTravelJournal(requestId, accounts);
      setPreview(result);
      setPreviewError(null);
      return result;
    } catch (error) {
      const details = error?.response?.data?.details;
      setPreview(null);
      setPreviewError({
        message: errorOf(error),
        missing: Array.isArray(details) ? details : [],
      });
      // Missing accounts are typed in the accounts panel, so open it.
      if (Array.isArray(details) && details.length) setOpenPanels(['accounts']);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // First view: the company's saved accounts (or the defaults) fill the form.
  useEffect(() => {
    if (!requestId) return;

    (async () => {
      const result = await loadPreview();
      form.setFieldsValue({
        accounts: result?.accounts,
        folder: result?.folder || '',
        date: dayjs(),
      });
      if (!result) {
        // Still show the form so the missing accounts can be typed in.
        form.setFieldsValue({ date: dayjs() });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  const handleRefresh = async () => {
    const values = await form.validateFields(['accounts']).catch(() => null);
    if (values) await loadPreview(form.getFieldValue('accounts'));
  };

  const bs = preview?.format === 'biznisoft';
  // The texts that name the target system.
  const k = (key) => (bs ? `journal.bs.${key}` : `journal.${key}`);

  const handleExport = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;

    try {
      setIsExporting(true);
      const blob = await API.requests.exportTravelJournal(requestId, {
        accounts: values.accounts,
        folder: (values.folder || '').trim(),
        date: values.date.format('YYYY-MM-DD'),
      });
      if (bs) {
        saveBlobAsFile(blob, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'NALOG.xlsx');
      } else {
        saveBlobAsFile(blob, 'application/vnd.ms-excel', 'NALOG.xls');
      }
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: t(k('exported'), { ns: 'officialTravel' }),
      });
      await loadPreview(values.accounts);
    } catch (error) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: errorOf(error),
      });
    } finally {
      setIsExporting(false);
    }
  };

  const fieldLabel = (field) =>
    t(`journal.accounts.${field}`, { ns: 'officialTravel' });

  const columns = [
    { title: t('journal.columns.account', { ns: 'officialTravel' }), dataIndex: 'account', key: 'account', width: 100 },
    { title: t('journal.columns.description', { ns: 'officialTravel' }), dataIndex: 'description', key: 'description' },
    { title: t('journal.columns.reference', { ns: 'officialTravel' }), dataIndex: 'reference', key: 'reference', width: 120 },
    { title: t('journal.columns.debit', { ns: 'officialTravel' }), dataIndex: 'debit', key: 'debit', align: 'right', width: 130, render: formatAmount },
    { title: t('journal.columns.credit', { ns: 'officialTravel' }), dataIndex: 'credit', key: 'credit', align: 'right', width: 130, render: formatAmount },
  ];

  const rows = (preview?.rows || []).map((row, index) => ({ ...row, key: index }));
  const total = (key) => rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

  return (
    <div className="grid gap-3 rounded-md border bg-zinc-50 p-4">
      <h4 className="font-semibold">
        {t(k('title'), { ns: 'officialTravel' })}
      </h4>
      <p className="text-sm text-zinc-600">
        {t(k('description'), { ns: 'officialTravel' })}
      </p>

      {preview?.abExport && (
        <AlertBox variant="info" className="mb-0">
          <p>
            {t('journal.lastExport', {
              ns: 'officialTravel',
              date: dayjs(preview.abExport.at).format(DATE_FORMATS.DISPLAY_WITH_TIME),
              name: preview.abExport.byName || '-',
              folder: preview.abExport.folder || '-',
            })}
          </p>
        </AlertBox>
      )}

      {previewError && (
        <AlertBox variant="warning" className="mb-0">
          <p>{previewError.message}</p>
          {previewError.missing.length > 0 && (
            <p className="mt-1">
              {previewError.missing
                .map((key) => {
                  const [group, field] = key.split('.');
                  return field
                    ? `${t(`journal.groups.${group}`, { ns: 'officialTravel' })}: ${fieldLabel(field)}`
                    : fieldLabel(group);
                })
                .join(', ')}
            </p>
          )}
        </AlertBox>
      )}

      <Form form={form} layout="vertical" autoComplete="off">
        <Collapse
          size="small"
          activeKey={openPanels}
          onChange={setOpenPanels}
          items={[
            {
              key: 'accounts',
              label: t('journal.accountsTitle', { ns: 'officialTravel' }),
              children: (
                <div className="grid gap-4">
                  <div>
                    <p className="mb-2 font-semibold">
                      {t('journal.groups.domestic', { ns: 'officialTravel' })}
                    </p>
                    <div className="grid gap-x-4 md:grid-cols-4">
                      {DOMESTIC_FIELDS.map((field) => (
                        <Form.Item key={field} name={['accounts', 'domestic', field]} label={fieldLabel(field)} rules={[ACCOUNT_RULE]}>
                          <Input />
                        </Form.Item>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="mb-2 font-semibold">
                      {t('journal.groups.foreign', { ns: 'officialTravel' })}
                    </p>
                    <div className="grid gap-x-4 md:grid-cols-5">
                      {FOREIGN_FIELDS.map((field) => (
                        <Form.Item key={field} name={['accounts', 'foreign', field]} label={fieldLabel(field)} rules={[ACCOUNT_RULE]}>
                          <Input />
                        </Form.Item>
                      ))}
                    </div>
                    <Form.Item name={['accounts', 'companyCard']} label={fieldLabel('companyCard')} rules={[ACCOUNT_RULE]} className="md:w-1/4">
                      <Input />
                    </Form.Item>
                  </div>

                  <div>
                    <p className="mb-1 font-semibold">
                      {t('journal.groups.countries', { ns: 'officialTravel' })}
                    </p>
                    <p className="mb-2 text-sm text-zinc-600">
                      {t('journal.countriesHint', { ns: 'officialTravel' })}
                    </p>
                    <Form.List name={['accounts', 'countries']}>
                      {(fields, { add, remove }) => (
                        <div className="grid gap-2">
                          {fields.map((field) => (
                            <div key={field.key} className="grid items-end gap-x-4 md:grid-cols-[2fr_1fr_1fr_auto]">
                              <Form.Item name={[field.name, 'countryCode']} label={t('journal.country', { ns: 'officialTravel' })} rules={[{ required: true }]}>
                                <Select showSearch optionFilterProp="label" options={countryOptions} />
                              </Form.Item>
                              <Form.Item name={[field.name, 'perDiemExpense']} label={fieldLabel('perDiemExpense')} rules={[ACCOUNT_RULE]}>
                                <Input />
                              </Form.Item>
                              <Form.Item name={[field.name, 'costExpense']} label={fieldLabel('costExpense')} rules={[ACCOUNT_RULE]}>
                                <Input />
                              </Form.Item>
                              <Form.Item>
                                <Button shape="circle" icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                              </Form.Item>
                            </div>
                          ))}
                          <div>
                            <Button shape="round" icon={<PlusOutlined />} onClick={() => add({ countryCode: '', perDiemExpense: '', costExpense: '' })}>
                              {t('journal.addCountry', { ns: 'officialTravel' })}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form.List>
                  </div>

                  <div className="flex justify-end">
                    <Button shape="round" loading={isLoading} onClick={handleRefresh}>
                      {t('journal.refresh', { ns: 'officialTravel' })}
                    </Button>
                  </div>
                </div>
              ),
            },
          ]}
        />

        {rows.length > 0 && (
          <Table
            className="mt-3"
            dataSource={rows}
            columns={columns}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: true }}
            loading={isLoading}
            summary={() => (
              <Table.Summary.Row className="font-semibold">
                <Table.Summary.Cell index={0} colSpan={3}>
                  {t('journal.total', { ns: 'officialTravel' })}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{formatAmount(total('debit'))}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">{formatAmount(total('credit'))}</Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        )}

        {(preview?.warnings || []).length > 0 && (
          <AlertBox variant="warning" className="mb-0 mt-3">
            {preview.warnings.map((warning) => (
              <p key={warning}>{warning}</p>
            ))}
          </AlertBox>
        )}

        <div className="mt-3 grid items-end gap-x-4 md:grid-cols-[1fr_1fr_auto]">
          {bs ? (
            <div />
          ) : (
            <Form.Item name="folder" label={t('journal.folder', { ns: 'officialTravel' })} rules={[{ required: true, whitespace: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="date" label={t('journal.date', { ns: 'officialTravel' })} rules={[{ required: true }]}>
            <DatePicker className="w-full" format={DATE_FORMATS.DISPLAY} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" shape="round" className="btn-primary" loading={isExporting} disabled={isExporting || !rows.length} onClick={handleExport}>
              {t(k('download'), { ns: 'officialTravel' })}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}
